import axios from 'axios';
import { MprcSpinnerIndicator } from 'components/MprcSpinnerIndicator';
import * as React from 'react';
import { SessionService } from 'services/sessionService';
import { NIL } from 'uuid';

interface IState {
    hasError: boolean;
    writingLog: boolean;
    logId: string | null;
    error: any;
}

interface IProps {
    children?: React.ReactNode;
}

export default class ErrorBoundary extends React.Component<IProps, IState> {
    private logEnabled = process.env.NODE_ENV !== 'development';

    constructor(props: IProps) {
        super(props);
        this.state = { hasError: false, error: null, logId: null, writingLog: false };
    }

    static ignoreErrors(message: string) {
        return message === 'ResizeObserver loop completed with undelivered notifications.' ||
            message === 'ResizeObserver loop limit exceeded';

    }

    static getDerivedStateFromError(error: any) {
        // Atualize o state para que a próxima renderização exiba a UI de fallback.
        if (!ErrorBoundary.ignoreErrors(error.message)) {
            return { hasError: true, error: error };
        }
    }

    private logEvent(message: string, stack?: string) {

        if (ErrorBoundary.ignoreErrors(message)) return;

        this.setState({ writingLog: true, hasError: true, logId: null, error: { message, stack } });

        axios({
            baseURL: SessionService.current.apiPaths.coreApi,
            url: "/api/App/WriteLog",
            method: "post",
            data: { message, stack },
            headers: {
                responseType: "json",
                "Content-Type": "application/json"
            }
        })
            .then((result: any) => {
                const { status, data } = result;
                var id = (status == 200) ? (data as string) : NIL;
                this.setState({ logId: id, writingLog: false });
            })
            .catch(() => {
                this.setState({ writingLog: false });
            });
    }

    public componentDidCatch(error: Error | string, errorInfo: any) {
        if (this.logEnabled) {
            this.logEvent(
                typeof (error) == "string" ? error : error.message,
                typeof (error) == "string" ? errorInfo?.componentStack : error.stack
            );
        }
    }

    public componentDidMount() {
        if (this.logEnabled) {
            window.onerror = async (event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error) => {
                if (!this.state.writingLog) {
                    //colno = colno || (window.event && (window.event as any).errorCharacter);
                    this.logEvent(event.toString(), error?.stack);
                }
                return false;
            };
        }
    }

    public render() {

        const { writingLog, hasError, logId, error } = this.state;

        if (hasError) {

            let errorMessage: string;
            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            } else {
                console.error(error);
                errorMessage = JSON.stringify(error);
            }

            let stack = error["stack"] ?? "";

            return this.logEnabled
                ? (
                    <div style={{ width: 600, margin: "0 auto", marginTop: 80, padding: 20, border: "solid 1px red" }}>
                        <div style={{ textAlign: "center" }}>

                            <h1 style={{ color: "red" }}>
                                <span>Ocorreu um erro durante a execução do aplicativo</span>
                                <br />
                                <span>An error occurred while running the application</span>
                            </h1>

                            <div style={{ marginTop: 40 }}>

                                <p>
                                    <span>Se o problema persistir, por favor, entre em contato com o administrador do sistema.</span>
                                    <br />
                                    <span>If the problem persists, please contact your system administrator.</span>
                                </p>

                                <p>
                                    <span>Por favor, retorne à página inicial clicando no botão abaixo.</span>
                                    <br />
                                    <span>Please return to the home page by clicking the button below.</span>
                                </p>

                                {
                                    writingLog
                                        ? <p><MprcSpinnerIndicator />Escrevendo log // Writing log...</p>
                                        : logId
                                            ? <p><b>Id para inspeção // Inspection Id:</b> {logId}</p>
                                            : null
                                }

                                <button
                                    style={{ padding: 10 }}
                                    type="button"
                                    onClick={() => { document.location.replace("/"); }}>
                                    <span>Página Inicial // Home Page</span>
                                </button>

                                <div style={{ marginTop: 20, textAlign: "left", color: "darkred" }}>
                                    <details>
                                        <div>
                                            <b>{errorMessage}</b>
                                        </div>
                                        <code>
                                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", textOverflow: "clip" }}>
                                                {stack}
                                            </pre>
                                        </code>
                                    </details>
                                </div>

                            </div>
                        </div >
                    </div >
                )
                : (
                    <div style={{ padding: 10, color: "darkred" }}>
                        <h1>Ocorreu um erro ao renderizar o componente</h1>
                        <div style={{ marginTop: 20, textAlign: "left" }}>
                            <div>
                                <b>{errorMessage}</b>
                            </div>
                            <code>
                                <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", textOverflow: "clip" }}>
                                    {stack}
                                </pre>
                            </code>
                        </div>
                    </div>
                );
        }

        return this.props.children;
    }
}
